<script>
    import { onMount } from "svelte";
    import * as d3 from "d3";

    export let data = []; // weekly data: [{week, count, court_id, state}, ...]
    export let showAdminBands = false;
    export let administrations = [];
    export let wrapperClass = "";
    export let cases;
    export let week_of;

    const margin = { top: 20, right: 5, bottom: 30, left: 5 };

    // SVG vs chart dimensions
    let svgWidth = 800;
    let svgHeight = 400;
    let wrapperEl;
    let chartWidth = svgWidth - margin.left - margin.right;
    let chartHeight = svgHeight - margin.top - margin.bottom;

    let svgEl, tooltipEl;
    let barsGroup;

    let lastWidth = 0;
    let x, y, xAxisGroup, yAxisGroup, annotationGroup, gridLinesGroup;
    let points = [];
    let dates = [];

    // Parse week string like "2025-01-06/2025-01-12" to get start date
    function parseWeek(weekStr) {
        const startDate = weekStr.split('/')[0];
        return d3.timeParse("%Y-%m-%d")(startDate);
    }

    // Aggregate weekly data by week (sum across all court_ids)
    $: points = aggregateByWeek(data);
    $: dates = points.map((d) => d.date);

    function getWidth() {
        return wrapperEl.node().getBoundingClientRect().width;
    }

    function updateWidth() {
        const newWidth = getWidth();
        lastWidth = newWidth;

        svgWidth = newWidth; // full SVG width
        svgHeight = Math.max(newWidth * 0.3, 325); // full SVG height
        chartWidth = svgWidth - margin.left - margin.right;
        chartHeight = svgHeight - margin.top - margin.bottom;

        if (svgEl && data.length) updateChart();
    }

    function aggregateByWeek(rows) {
        if (!rows.length) return [];
        
        // Group by week and sum counts
        const rollup = d3.rollup(
            rows,
            (v) => d3.sum(v, d => d.count),
            (d) => d.week
        );
        
        // Convert to array and sort by date
        return Array.from(rollup, ([week, count]) => ({
            date: parseWeek(week),
            week: week,
            count: count
        })).sort((a, b) => a.date - b.date);
    }

    function drawAdministrationBands(svg, x, height) {
        if (!showAdminBands) return;
        const bandGroup = svg.select(".admin-bands");
        const bandHeight = chartHeight;

        const bands = bandGroup.selectAll("rect").data(administrations);
        bands
            .join(
                (enter) => enter.append("rect"),
                (update) => update,
                (exit) => exit.remove(),
            )
            .attr("x", (d) => x(d.start))
            .attr("y", margin.top)
            .attr("width", (d) => x(d.end) - x(d.start))
            .attr("height", bandHeight)
            .attr("fill", (d) =>
                d.dem ? "rgba(103,142,230,.3)" : "rgba(218,53,43,.3)",
            )
            .attr("opacity", 0.35);

        const labels = bandGroup.selectAll("text").data(administrations);
        labels
            .join(
                (enter) => enter.append("text"),
                (update) => update,
                (exit) => exit.remove(),
            )
            .attr("x", (d) => (x(d.start) + x(d.end)) / 2)
            .attr("y", margin.top - 5)
            .attr("text-anchor", "middle")
            .attr("class", "chart-admin-label")
            .text((d) => d.label);
    }

    function styleAxis(g, isYAxis = false) {
        // Remove the axis line (domain)
        if (isYAxis) {
            g.select(".domain").remove();
            // Remove tick lines on y-axis
            
        } else {
            g.select(".domain")
                .attr("stroke", "var(--border-color)")
                .attr("stroke-width", 1);
        }
        g.selectAll(".tick line").remove();
        g.selectAll(".tick text")
            .style("font-size", "var(--scale-1)")
            .style("fill", "var(--gray-50)");
    }

    function initChart() {
        if (!data.length) return;

        const svg = d3.select(svgEl);
        svg.selectAll("*").remove();

        svg.append("g").attr("class", "admin-bands");
        gridLinesGroup = svg.append("g").attr("class", "grid-lines");
        xAxisGroup = svg.append("g");
        yAxisGroup = svg.append("g");

        barsGroup = svg.append("g").attr("class", "bars");

        annotationGroup = svg.append("g").attr("class", "annotations");

        updateChart();
    }

    function updateChart() {
        if (!data.length) return;

        // Calculate bar width based on the time range and number of points
        const barWidth = Math.max(2, (chartWidth / points.length) * 0.9);
        
        // Adjust x scale range to account for half bar width on each end
        x = d3
            .scaleTime()
            .domain(d3.extent(points, (d) => d.date))
            .range([margin.left + barWidth / 2, margin.left + chartWidth - barWidth / 2]);
        
        y = d3
            .scaleLinear()
            .domain([0, d3.max(points, (d) => d.count)])
            .nice()
            .range([margin.top + chartHeight, margin.top]);

        // Generate ticks but skip the first one
        const xTicks = x.ticks(chartWidth < 600 ? 3 : 5).slice(1);
        
        xAxisGroup
            .attr("transform", `translate(0,${margin.top + chartHeight})`)
            .call(
                d3
                    .axisBottom(x)
                    .tickValues(xTicks)
                    .tickFormat(d3.timeFormat("%b %Y"))
                    .tickSizeOuter(0),
            )
            .call(styleAxis);

        const yTicks = y.ticks(3);
        const maxTick = d3.max(yTicks);     
        yAxisGroup
            .attr("transform", `translate(${margin.left},0)`)
            .call(
                d3
                    .axisLeft(y)
                    .ticks(3)
                    .tickSizeOuter(0)
                    .tickFormat(d => {
                        const thousands = Math.round(d / 1000);
                        if (thousands <= 0) return "";

                        return d === maxTick
                        ? `${thousands}K cases`
                        : `${thousands}K`;
                    }),
                            )
            .call((g) => styleAxis(g, true));
        
        // Reposition y-axis labels to appear above grid lines and extend rightward
        yAxisGroup.selectAll(".tick text")
            .attr("text-anchor", "start")
            .attr("x", 0)
            .attr("dy", "-0.5em");

        // Draw horizontal grid lines at y-axis tick positions
        const svg = d3.select(svgEl);
        const gridLines = svg.select(".grid-lines");
        
        gridLines.selectAll("line")
            .data(yTicks)
            .join("line")
            .attr("x1", margin.left)
            .attr("x2", margin.left + chartWidth)
            .attr("y1", d => y(d))
            .attr("y2", d => y(d))
            .attr("stroke", "var(--border-color)")
            .attr("stroke-width", 1)
            .attr("opacity", 0.25);

        drawAdministrationBands(d3.select(svgEl), x, chartHeight);

        // Draw bars
        const bars = barsGroup.selectAll("rect").data(points);

        bars.join(
            (enter) => enter.append("rect"),
            (update) => update,
            (exit) => exit.remove(),
        )
            .attr("x", (d) => x(d.date) - barWidth / 2)
            .attr("y", (d) => y(d.count))
            .attr("width", barWidth)
            .attr("height", (d) => margin.top + chartHeight - y(d.count))
            .attr("fill", (d, i) => "var(--bar-chart-color)")
            .attr("rx", 2)
            .attr("ry", 2)
            .attr("class", "chart-bar")
            .style("cursor", "pointer")
            .style("transition", "opacity 0.2s ease")
            .on("mouseenter", function() {
                d3.select(this).style("opacity", 0.7);
            })
            .on("mouseleave", function() {
                d3.select(this).style("opacity", 1);
            });

        if (!showAdminBands) {
            bars
                .on("mouseenter", function(event, d) {
                    d3.select(this).style("opacity", 0.7);
                    tooltipEl.style.opacity = 1;
                    tooltipEl.innerHTML = `
                    <div><strong>${d.count.toLocaleString()} ${cases}</strong></div>
                    <div class="week-annotation">${week_of} ${d3.timeFormat("%b %d, %Y")(d.date)}</div>
                    `;
                })
                .on("mousemove", (event) => {
                    const tooltipNode = tooltipEl;
                    const tooltipWidth = tooltipNode.offsetWidth;
                    const tooltipHeight = tooltipNode.offsetHeight;
                    const pageWidth = window.innerWidth;
                    const pageHeight = window.innerHeight + window.scrollY;

                    // Initial left position logic
                    let left = event.pageX + tooltipWidth + 20 > pageWidth
                    ? event.pageX - tooltipWidth - 10
                    : event.pageX + 10;

                    // If tooltip would go off the left edge, center it horizontally
                    if (left < 0) {
                        left = (pageWidth - tooltipWidth) / 2;
                    }

                    // Position above cursor if would go off bottom edge
                    const top = event.pageY + tooltipHeight + 10 > pageHeight
                    ? event.pageY - tooltipHeight - 10
                    : event.pageY - 30;
                        
                    tooltipEl.style.left = `${left}px`;
                    tooltipEl.style.top = `${top}px`;
                })
                .on("mouseleave", function() {
                    d3.select(this).style("opacity", 1);
                    tooltipEl.style.opacity = 0;
                });
        }

        // Add annotation for second to last bar only
        const annotations = svg.select(".annotations");
        annotations.selectAll("*").remove();
        
        if (points.length >= 2) {
            const secondToLast = points[points.length - 1];
            const annotation = annotations
                .append("g")
                .attr(
                    "transform",
                    `translate(${x(secondToLast.date)},${y(secondToLast.count)})`,
                );

            annotation
                .append("text")
                .attr("x", 5)
                .attr("y", -10)
                .attr("text-anchor", "end")
                .attr("class", "end-annotation")
                .text(`${secondToLast.count.toLocaleString()}`);
        }
    }

    onMount(() => {
        wrapperEl = d3.select(wrapperClass);
        initChart();
        updateWidth();
        let resizeTimeout;
        const listener = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(updateWidth, 50);
        };
        window.addEventListener("resize", listener);
        return () => window.removeEventListener("resize", listener);
    });

    $: svgEl && data.length && updateChart();
</script>

<svg bind:this={svgEl} width={svgWidth} height={svgHeight}></svg>
<div class="tooltip" bind:this={tooltipEl}></div>
